/**
 * Layer 3: Theme definitions.
 * Each theme maps semantic token names to primitive palette values.
 * Used to generate CSS (themes.scss) and for JS when needed.
 */

import { PRIMITIVE_PALETTES } from './colors.primitives';

const n = PRIMITIVE_PALETTES.neutral;
const brand = PRIMITIVE_PALETTES.brand;
const blue = PRIMITIVE_PALETTES.blue;
const red = PRIMITIVE_PALETTES.red;
const yellow = PRIMITIVE_PALETTES.yellow;
const orange = PRIMITIVE_PALETTES.orange;
const green = PRIMITIVE_PALETTES.green;
const black = PRIMITIVE_PALETTES.black;

/** @typedef {'brand'|'blue'|'red'|'yellow'|'orange'|'green'} ThemeName */

/** @typedef {Record<string, string>} SemanticColorMap */

/** @typedef {{ name: ThemeName, semanticColors: SemanticColorMap }} ThemeDefinition */

/**
 * Build a theme by mapping semantic tokens to palette values.
 * Semantic keys match those in colors.semantic.js and CSS variables:
 *   --color-{semantic-key}
 *
 * @param {ThemeName} name
 * @param {import('./colors.primitives').Palette} basePalette
 * @returns {ThemeDefinition}
 */
const createTheme = (name, basePalette) => ({
  name,
  semanticColors: {
    'base-800': basePalette[800],
    'base-700': basePalette[700],
    'base-600': basePalette[600],
    'base-500': basePalette[500],
    'base-400': basePalette[400],
    'base-300': basePalette[300],
    'base-200': basePalette[200],
    'base-100': basePalette[100],
    'base-50': basePalette[50],
    // Surface
    'surface-page': '#FFFFFF',
    'surface-subtle': n[50],
    'surface-card': n[75],
    'surface-elevated': '#FFFFFF',
    'surface-inset': n[25],
    'surface-overlay': black[50],
    'surface-inverse': n[600],

    // Text
    'text-primary': n[700],
    'text-secondary': n[500],
    'text-tertiary': n[400],
    'text-disabled': n[300],

    // Text inverse
    'text-inverse-primary': '#FFFFFF',
    'text-inverse-secondary': n[200],
    'text-inverse-tertiary': n[300],
    'text-inverse-disabled': n[400],

    // Links
    'link-default': basePalette[500],
    'link-hover': basePalette[700],
    'link-pressed': basePalette[800],
    'link-visited': basePalette[400],
    'link-disabled': n[400],

    // Borders & dividers
    'border-light': n[150],
    'border-medium': n[175],
    'border-subtle': n[200],
    'border-default': n[300],
    'border-strong': n[500],
    'border-focus': basePalette[500],
    'border-disabled': n[100],
    'border-inverse': n[600],

    // Icons
    'icon-default': n[700],
    'icon-inverse': '#FFFFFF',
    'icon-subtle': n[500],
    'icon-disabled': n[300],
    'icon-brand': basePalette[500],

    // Actions (primary / secondary / tertiary)
    'action-primary': basePalette[500],
    'action-primary-hover': basePalette[700],
    'action-primary-pressed': basePalette[800],
    'action-primary-disabled': basePalette[200],

    'action-secondary': '#FFFFFF',
    'action-secondary-hover': n[50],
    'action-secondary-pressed': n[100],
    'action-secondary-disabled': '#FFFFFF',

    'action-tertiary': '#FFFFFF',
    'action-tertiary-hover': basePalette[50],
    'action-tertiary-pressed': basePalette[100],
    'action-tertiary-disabled': '#FFFFFF',

    // Feedback (system messages) — fixed across themes
    'feedback-success-text': green[700],
    'feedback-success-surface': green[50],
    'feedback-success-border': green[300],

    'feedback-error-text': red[700],
    'feedback-error-surface': red[50],
    'feedback-error-border': red[300],

    'feedback-warning-text': yellow[700],
    'feedback-warning-surface': yellow[50],
    'feedback-warning-border': yellow[300],

    'feedback-info-text': blue[700],
    'feedback-info-surface': blue[50],
    'feedback-info-border': blue[300],

    // Pill (content-type indicators — fixed across themes)
    'pill-news-text': blue[800],
    'pill-news-surface': blue[100],
    'pill-news-border': blue[200],

    'pill-job-text': brand[800],
    'pill-job-surface': brand[100],
    'pill-job-border': brand[200],

    'pill-sec-text': red[800],
    'pill-sec-surface': red[200],
    'pill-sec-border': red[300],

    // Status (badges, dots, indicators, surfaces, and text)
    'status-neutral': n[500],
    'status-active': basePalette[500],
    'status-success': green[600],
    'status-warning': yellow[600],
    'status-error': red[600],
    'status-inactive': n[400],
    'status-pending': blue[500],
    'status-active-surface': green[25],
    'status-active-text': green[675],
    'status-active-indicator': green[450],
    'status-inactive-surface': n[40],
    'status-inactive-text': n[550],
    'status-inactive-indicator': n[450],
    'status-trial-surface': yellow[25],
    'status-trial-text': n[650],
    'status-trial-indicator': yellow[650],
    'status-trial-expired-indicator': red[650],
    'status-premium-surface': green[75],
    'status-premium-indicator': green[650],

    // Focus & accessibility
    'focus-ring': basePalette[500],
    'focus-ring-error': red[500],
    'focus-background': basePalette[50],
    'focus-disabled': n[500],
  },
});

/** @type {Record<ThemeName, ThemeDefinition>} */
export const THEMES = {
  brand: createTheme('brand', brand),
  blue: createTheme('blue', blue),
  red: createTheme('red', red),
  yellow: createTheme('yellow', yellow),
  orange: createTheme('orange', orange),
  green: createTheme('green', green),
};

/** @type {ThemeName[]} */
export const THEME_NAMES = Object.keys(THEMES);

/**
 * Theme payload from backend or custom source.
 * Same semantic keys as THEMES[].semanticColors (see colors.semantic.js).
 * @typedef {{ id?: string, name?: string, semanticColors: SemanticColorMap }} CustomThemePayload
 */

/**
 * Apply a theme by writing CSS variables to the DOM.
 * Use this for both built-in themes (THEMES[name].semanticColors) and
 * backend/custom themes (payload.semanticColors).
 * Backend must send semanticColors with keys matching our semantic token names
 * (e.g. surface-page, action-primary, text-primary). See colors.semantic.js.
 *
 * @param {SemanticColorMap} semanticColors - Map of token name → hex/value
 * @param {HTMLElement} [targetElement] - Defaults to document.documentElement
 */
export function applyThemeToDom(semanticColors, targetElement) {
  if (typeof document === 'undefined' || !semanticColors) return;
  const el = targetElement ?? document.documentElement;
  Object.entries(semanticColors).forEach(([key, value]) => {
    el.style.setProperty(`--color-${key}`, value);
  });
}
